import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import MainSectionInner from '../../layout/components/MainSectionInner';
import { MediaQueryView } from '../../layout/components/MediaQueryView';
import { GRID_BREAKPOINT_LARGE, GRID_BREAKPOINT_SMALL } from '../../layout/constants';
import { SearchSelectWrapper } from '../../search/components/SearchSelectWrapper';
import { StorefrontHeaderView } from './StorefrontHeaderView';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledGridWrapper = styled.div.withConfig({
  displayName: "StorefrontHeader__StyledGridWrapper"
})(["margin:auto;width:", ";@media only screen and (min-width:", ") and (max-width:", "){width:80%;}@media only screen and (max-width:", "){width:95%;}"], ({
  $sticky
}) => $sticky ? '100% !important' : '60%', GRID_BREAKPOINT_SMALL, GRID_BREAKPOINT_LARGE, GRID_BREAKPOINT_SMALL);
export function StorefrontHeader({
  backgroundImage,
  children,
  collapsedTitle,
  compact = false,
  color,
  divider,
  gradientAngle,
  onStickyChange,
  renderedButtonRow,
  renderedBreadcrumbs,
  renderBreadcrumbsInTitle = false,
  subtitle,
  title,
  testId,
  use = 'default'
}) {
  const [isSticky, setIsSticky] = useState(compact);
  const mainContainerRef = useRef(null);
  const searchBarRef = useRef(null);

  // Sometimes there is a global banner, such as for app dev accounts. This ensures
  // the sticky header sticks below the banner, rather than glitching on top of it.
  const stickyTopOffsetRef = useRef(0);
  const isNewAppNav = document.body.classList.contains('sticky-global-toolbar');
  const renderedSprocketIcon = /*#__PURE__*/_jsx(SprocketIcon, {
    color: "fill-brand-default",
    size: "600"
  });
  useEffect(() => {
    if (onStickyChange) {
      onStickyChange(SyntheticEvent(isSticky));
    }
    /*
    TODO - fix:
    In the interest of time I'm only including isSticky in the dependency array because
    if onStickyChange is defined in-line it changes with every render and causes this
    hook to run infinitely. When we re-refactor this component we can revisit this issue.
     */
  }, [isSticky]); // eslint-disable-line

  // set up sticky scroll behavior
  useEffect(() => {
    var _ref, _document$getElementB, _globalNav$clientHeig;
    if (!mainContainerRef.current || !searchBarRef.current) return;
    const mainContainerTop = mainContainerRef.current.getBoundingClientRect().top;
    const globalNav = (_ref = (_document$getElementB = document.getElementById('hs-global-toolbar')) !== null && _document$getElementB !== void 0 ? _document$getElementB :
    // in-app nav
    document.getElementById('hsg-nav-global')) !== null && _ref !== void 0 ? _ref :
    // legacy public nav
    document.querySelector('.hs-global-nav-header'); // public nav

    // Getting the height here and subtracting it later will place the sticky
    // storefront header on TOP of the global nav, rather than below it.
    const globalNavHeight = (_globalNav$clientHeig = globalNav === null || globalNav === void 0 ? void 0 : globalNav.clientHeight) !== null && _globalNav$clientHeig !== void 0 ? _globalNav$clientHeig : 0;
    stickyTopOffsetRef.current = mainContainerTop - globalNavHeight;

    // distance between top of header and top of search bar
    const searchBarDistance = searchBarRef.current.getBoundingClientRect().top - mainContainerTop;

    // new app nav has its own sticky header, so we need to account for that
    const stickyThreshold = isNewAppNav ? -searchBarDistance + mainContainerTop : -searchBarDistance;

    // called every time the scroll position changes
    const scrollHandler = () => {
      const scrollPos = mainContainerRef.current.getBoundingClientRect().top;
      setIsSticky(scrollPos < stickyThreshold);
    };

    // register/unregister scroll handler
    window.addEventListener('scroll', scrollHandler, true);
    return () => window.removeEventListener('scroll', scrollHandler, true);

    // IMPORTANT NOTE: isNewAppNav may change over time as the page loads,
    // so it's important to recalculate the stickyThreshold when it changes
  }, [isNewAppNav]); // eslint-disable-line

  // set sticky to false when location changes (because we'll be back at the top)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsSticky(false);
    if (onStickyChange) onStickyChange(SyntheticEvent(false));
  }, [window.location.search, window.location.href]); // eslint-disable-line

  return /*#__PURE__*/_jsxs(StorefrontHeaderView, {
    as: "header",
    backgroundImage: backgroundImage,
    color: color,
    "data-test-id": testId,
    divider: divider,
    gradientAngle: gradientAngle,
    ref: mainContainerRef,
    sticky: isSticky,
    children: [!compact && use !== 'collapsed' && /*#__PURE__*/_jsx(View, {
      paddingBlockStart: "700",
      paddingInline: "400",
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          direction: "column",
          children: [renderedButtonRow && /*#__PURE__*/_jsx(MediaQueryView, {
            alignSelf: "end",
            min: "md",
            children: renderedButtonRow
          }), renderBreadcrumbsInTitle && renderedBreadcrumbs && /*#__PURE__*/_jsx(View, {
            alignSelf: "start",
            marginBlockEnd: "400",
            children: renderedBreadcrumbs
          }), /*#__PURE__*/_jsx(Text, {
            "data-test-id": "titleText",
            color: color === 'white' ? 'text-core-default' : 'text-core-on-fill-default',
            textAlign: "center",
            children: title
          }), subtitle ? /*#__PURE__*/_jsx(Text, {
            color: color === 'white' ? 'text-core-default' : 'text-core-on-fill-default',
            "data-test-id": "subtitleText",
            marginBlockEnd: "800",
            paddingBlockEnd: isSticky ? '1100' : '0',
            textAlign: "center",
            typography: "heading-200",
            children: subtitle
          }) : null]
        })
      })
    }), /*#__PURE__*/_jsx(SearchSelectWrapper, {
      collapsed: use === 'collapsed',
      color: color,
      gradientAngle: gradientAngle,
      isNewAppNav: isNewAppNav,
      isSticky: isSticky,
      ref: searchBarRef
      // eslint-disable-next-line react-hooks/refs
      ,
      stickyTopOffset: stickyTopOffsetRef.current,
      children: /*#__PURE__*/_jsxs(MainSectionInner, {
        children: [!renderBreadcrumbsInTitle && renderedBreadcrumbs && /*#__PURE__*/_jsx(View, {
          marginInlineStart: "400",
          marginBlockEnd: "300",
          children: renderedBreadcrumbs
        }), /*#__PURE__*/_jsxs(StyledGridWrapper, {
          $sticky: isSticky || use === 'collapsed',
          children: [use === 'collapsed' && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(MediaQueryView, {
              min: "sm",
              children: /*#__PURE__*/_jsxs(Grid, {
                marginInline: "0",
                children: [/*#__PURE__*/_jsx(GridItem, {
                  size: {
                    md: 6,
                    lg: 4,
                    xl: 3
                  },
                  children: /*#__PURE__*/_jsx(MediaObject, {
                    align: "baseline",
                    itemLeft: renderedSprocketIcon,
                    paddingBlockStart: "200",
                    children: /*#__PURE__*/_jsx(MediaQueryView, {
                      min: "md",
                      children: /*#__PURE__*/_jsx(Text, {
                        as: "h4",
                        color: color === 'white' ? 'text-core-default' : 'text-core-on-fill-default',
                        marginBlockEnd: "0",
                        children: /*#__PURE__*/_jsx(TruncateString, {
                          children: collapsedTitle
                        })
                      })
                    })
                  })
                }), /*#__PURE__*/_jsx(GridItem, {
                  paddingInline: "0",
                  size: {
                    md: 6,
                    lg: 8,
                    xl: 9
                  },
                  children: children
                })]
              })
            }), /*#__PURE__*/_jsx(MediaQueryView, {
              max: "sm",
              children: /*#__PURE__*/_jsxs(Grid, {
                marginInline: "0",
                children: [/*#__PURE__*/_jsx(GridItem, {
                  size: {
                    xs: 10,
                    sm: 10
                  },
                  children: /*#__PURE__*/_jsx(Text, {
                    as: "h4",
                    color: color === 'white' ? 'text-core-default' : 'text-core-on-fill-default',
                    marginBlockEnd: "0",
                    paddingBlockStart: "300",
                    children: /*#__PURE__*/_jsx(TruncateString, {
                      children: collapsedTitle
                    })
                  })
                }), /*#__PURE__*/_jsx(GridItem, {
                  paddingInline: "0",
                  size: {
                    xs: 2,
                    sm: 2
                  },
                  children: children
                })]
              })
            })]
          }), use !== 'collapsed' && isSticky && /*#__PURE__*/_jsxs(Grid, {
            marginInline: "0",
            children: [/*#__PURE__*/_jsx(GridItem, {
              size: {
                xs: 2,
                md: 4,
                lg: 3
              },
              children: /*#__PURE__*/_jsx(MediaObject, {
                align: "baseline",
                itemLeft: renderedSprocketIcon,
                paddingBlockStart: "200",
                children: /*#__PURE__*/_jsx(MediaQueryView, {
                  inclusive: false,
                  min: "md",
                  children: /*#__PURE__*/_jsx(Text, {
                    as: "h4",
                    color: color === 'white' ? 'text-core-default' : 'text-core-on-fill-default',
                    marginBlockEnd: "0",
                    children: /*#__PURE__*/_jsx(TruncateString, {
                      children: collapsedTitle
                    })
                  })
                })
              })
            }), /*#__PURE__*/_jsx(GridItem, {
              paddingInline: "0",
              size: {
                xs: 10,
                sm: 10,
                md: 8,
                lg: 9
              },
              children: children
            })]
          }), use !== 'collapsed' && !isSticky && /*#__PURE__*/_jsx(Grid, {
            marginInline: "0",
            children: /*#__PURE__*/_jsx(GridItem, {
              paddingInline: "0",
              size: 12,
              children: children
            })
          })]
        })]
      })
    })]
  });
}
export default StorefrontHeader;